import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useT } from '../i18n';
import { ApiError, useRegister } from '../api';
import { Button, Input } from '../ui';
import { Brand } from '../app/Brand';
import { LoginControls } from './LoginControls';
import { useAuthStore, useIsAuthenticated } from './store';
import s from './LoginPage.module.css';

export function RegisterPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const authed = useIsAuthenticated();
  const setSession = useAuthStore((x) => x.setSession);
  const register = useRegister();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (authed) return <Navigate to="/" replace />;

  const messageFor = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'email_already_registered') return t('register.error.emailTaken');
    return t('register.error.generic');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await register.mutateAsync({ email, password, name, orgName });
      if (res.status === 'ok') {
        setSession(res.token, res.user);
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(messageFor(err));
    }
  };

  return (
    <div className={s.page}>
      <aside className={s.hero}>
        <Brand />
        <div className={s.pitch}>
          <h1>
            {t('login.hero.title')} <span>{t('login.hero.accent')}</span>
          </h1>
          <p>{t('login.hero.text')}</p>
        </div>
        <ul className={s.points}>
          <li>{t('login.hero.p1')}</li>
          <li>{t('login.hero.p2')}</li>
          <li>{t('login.hero.p3')}</li>
        </ul>
      </aside>

      <main className={s.panel}>
        <div className={s.controls}>
          <LoginControls />
        </div>
        <div className={s.formWrap}>
          <form onSubmit={submit} className={s.form} noValidate>
            <div className={s.head}>
              <h2>{t('register.title')}</h2>
              <p>{t('register.subtitle')}</p>
            </div>
            {error && (
              <p className={s.alert} role="alert">
                {error}
              </p>
            )}
            <Input label={t('register.name')} value={name} onChange={(e) => setName(e.target.value)} required autoFocus dir="ltr" />
            <Input label={t('register.email')} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" />
            <Input
              label={t('register.password')}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              hint={t('register.passwordHint')}
              dir="ltr"
            />
            <Input label={t('register.orgName')} value={orgName} onChange={(e) => setOrgName(e.target.value)} required dir="ltr" />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={register.isPending}
              iconEnd={<ArrowRight size={18} className="flip-rtl" />}
              fullWidth
              disabled={!name || !email || password.length < 8 || !orgName}
            >
              {t('register.submit')}
            </Button>
            <p className={s.demo}>
              {t('register.hasAccount')} <Link to="/login">{t('register.signIn')}</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
